import React, { useState } from 'react';
import { cn } from "@/lib/utils";
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authService } from '@/services/implementations/AuthService';
import { toast } from 'sonner';
import { useTranslation } from '@/contexts/I18nContext';

interface ForgotPasswordFormProps {
  onSwitchToLogin?: () => void;
  onSwitchToRegister?: () => void;
  className?: string;
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  onSwitchToLogin,
  className,
  ...props
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    verificationCode: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<'email' | 'verification' | 'reset'>('email');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSendCode = async () => {
    if (!formData.email) {
      toast.error(t('auth.pleaseEnterEmail'));
      return;
    }

    setIsLoading(true);

    try {
      await authService.sendOTP({
        email: formData.email,
        purpose: 'forgot_password',
      });
      setStep('verification');
      toast.success(t('auth.passwordResetCodeSent'));
    } catch (err: any) {
      toast.error(err.message || t('auth.failedToSendCode'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!formData.verificationCode) {
      toast.error(t('auth.pleaseEnterVerificationCode'));
      return;
    }

    setIsLoading(true);

    try {
      await authService.verifyOTP({
        email: formData.email,
        code: formData.verificationCode,
        purpose: 'forgot_password',
      });
      setStep('reset');
      toast.success(t('auth.codeVerified'));
    } catch (err: any) {
      toast.error(err.message || t('auth.invalidVerificationCode'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error(t('auth.passwordsDoNotMatch'));
      return;
    }
    if (formData.newPassword.length < 8) {
      toast.error(t('auth.passwordTooShort'));
      return;
    }

    setIsLoading(true);

    try {
      await authService.resetPassword({
        email: formData.email,
        code: formData.verificationCode,
        newPassword: formData.newPassword,
      });
      toast.success(t('auth.passwordResetSuccessful'));
      setTimeout(() => {
        onSwitchToLogin?.();
      }, 2000);
    } catch (err: any) {
      toast.error(err.message || t('auth.passwordResetFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    switch (step) {
      case 'email':
        await handleSendCode();
        break;
      case 'verification':
        await handleVerifyCode();
        break;
      case 'reset':
        await handleResetPassword();
        break;
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case 'email':
        return t('auth.resetPassword');
      case 'verification':
        return t('auth.verifyYourEmail');
      case 'reset':
        return t('auth.setNewPassword');
    }
  };

  const getStepDescription = () => {
    switch (step) {
      case 'email':
        return t('auth.enterEmailForReset');
      case 'verification':
        return t('auth.enterVerificationCode');
      case 'reset':
        return t('auth.setNewPassword');
    }
  };

  const isFormValid = () => {
    switch (step) {
      case 'email':
        return formData.email;
      case 'verification':
        return formData.verificationCode;
      case 'reset':
        return formData.newPassword && formData.confirmPassword;
      default:
        return false;
    }
  };

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card className="overflow-hidden p-0">
        <CardContent className="grid p-0 md:grid-cols-2">
          <form onSubmit={handleSubmit} className="p-6 md:p-8">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col items-center text-center">
                <h1 className="text-2xl font-bold">
                  {getStepTitle()}
                </h1>
                <p className="text-muted-foreground text-balance">
                  {getStepDescription()}
                </p>
              </div>

              {step === 'email' && (
                <div className="grid gap-3">
                  <Label htmlFor="email">{t('auth.email')}</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder={t('auth.emailPlaceholder')}
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    disabled={isLoading}
                  />
                </div>
              )}

              {step === 'verification' && (
                <>
                  <div className="grid gap-3">
                    <Label htmlFor="verificationCode">{t('auth.verificationCode')}</Label>
                    <Input
                      id="verificationCode"
                      name="verificationCode"
                      type="text"
                      placeholder={t('auth.verificationCodePlaceholder')}
                      value={formData.verificationCode}
                      onChange={handleInputChange}
                      maxLength={6}
                      required
                      disabled={isLoading}
                    />
                    <p className="text-xs text-muted-foreground">
                      {t('auth.codeSentTo').replace('{email}', formData.email)}
                    </p>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handleSendCode}
                    disabled={isLoading}
                  >
                    {t('auth.resendVerificationCode')}
                  </Button>
                </>
              )}

              {step === 'reset' && (
                <>
                  <div className="grid gap-3">
                    <Label htmlFor="newPassword">{t('auth.newPassword')}</Label>
                    <Input
                      id="newPassword"
                      name="newPassword"
                      type="password"
                      placeholder={t('auth.newPasswordPlaceholder')}
                      value={formData.newPassword}
                      onChange={handleInputChange}
                      required
                      disabled={isLoading}
                    />
                  </div>

                  <div className="grid gap-3">
                    <Label htmlFor="confirmPassword">{t('auth.confirmNewPassword')}</Label>
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      placeholder={t('auth.confirmNewPasswordPlaceholder')}
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      required
                      disabled={isLoading}
                    />
                  </div>
                </>
              )}

              {/* 提交按钮 */}
              <Button
                type="submit"
                className="w-full"
                disabled={!isFormValid() || isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {step === 'email' && t('auth.sendingCodeLoading')}
                    {step === 'verification' && t('auth.verifying')}
                    {step === 'reset' && t('auth.resettingPassword')}
                  </div>
                ) : (
                  <>
                    {step === 'email' && t('auth.sendVerificationCode')}
                    {step === 'verification' && t('auth.verifyCode')}
                    {step === 'reset' && t('auth.resetPasswordButton')}
                  </>
                )}
              </Button>

              {/* 返回登录 */}
              <div className="text-center text-sm">
                {t('auth.rememberPassword')}{" "}
                <button
                  type="button"
                  onClick={onSwitchToLogin}
                  className="underline underline-offset-4"
                >
                  {t('auth.backToLogin')}
                </button>
              </div>

              {/* 返回上一步 */}
              {(step === 'verification' || step === 'reset') && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => {
                      if (step === 'verification') {
                        setStep('email');
                      } else if (step === 'reset') {
                        setStep('verification');
                      }
                    }}
                    className="text-sm underline underline-offset-4"
                  >
                    {t('auth.backToPreviousStep')}
                  </button>
                </div>
              )}
            </div>
          </form>
          <div className="bg-muted relative hidden md:block">
            <img
              src="/placeholder.svg"
              alt="Password reset illustration"
              className="absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale"
            />
          </div>
        </CardContent>
      </Card>
      <div className="text-muted-foreground text-center text-xs text-balance">
        {t('auth.termsAndPrivacy')}{" "}
        <a href="#" className="underline underline-offset-4 hover:text-primary">
          {t('auth.termsOfService')}
        </a>{" "}
        {t('auth.and')}{" "}
        <a href="#" className="underline underline-offset-4 hover:text-primary">
          {t('auth.privacyPolicy')}
        </a>.
      </div>
    </div>
  );
}; 